/** Display helpers for property feature rules (Features tab + dispatch desk). */

import {
  CITATION_APPLY_BY,
  CITATION_BY,
  CITATION_PERIOD,
  SAFELISTING_BY,
  SAFELISTING_PERIOD,
} from "@/lib/constansts";
import {resolveHouseholdSelectOptions} from "@/src/modules/properties/lib/household-property-rules";

/** Numeric permit cap — not a 0/1 toggle; API key/label varies by property type template. */
function isPermanentParkingMaxPermitsRule(key: string, name = ""): boolean {
  const k = key.toLowerCase();
  const label = name.toLowerCase();
  if (k.includes("period") || (/\bperiod\b/.test(label) && /\bby\b/.test(label))) return false;
  if (
    k === "permanent_parking_max_permits" ||
    k.includes("max_permit") ||
    k.includes("maximum_permit") ||
    k.includes("maximun")
  ) {
    return true;
  }
  if (!/\bpermit/.test(label)) return false;
  return /\bmax(im)?um(um|n)?\b/.test(label) || /\bnumber\b/.test(label);
}

/**
 * Permanent parking rules stored as `type: "number"` with values 0/1 (API convention).
 * Excludes permit caps and period selectors.
 */
export function isPermanentParkingYesNoRuleKey(key: string, name = ""): boolean {
  if (isPermanentParkingMaxPermitsRule(key, name)) return false;
  if (key.includes("period") || key.includes("rolling") || key.endsWith("_days")) return false;
  const k = key.toLowerCase();
  const label = name.toLowerCase();
  if (!k.startsWith("permanent_parking_") && !label.includes("permanent parking")) return false;
  return k.endsWith("_required") || k.endsWith("_final_say");
}

function getPermanentParkingRuleSortIndex(key: string, name = ""): number {
  if (isPermanentParkingMaxPermitsRule(key, name)) return 0;

  const k = key.toLowerCase();
  const label = name.toLowerCase();

  if (k.includes("period") || (/\bperiod\b/.test(label) && /\bby\b/.test(label))) return 1;
  if (
    (k.includes("approval") && k.includes("required") && !k.includes("final")) ||
    (/\bapproval\b/.test(label) && /\brequired\b/.test(label) && !/\bfinal\b/.test(label))
  ) {
    return 2;
  }
  if (
    (k.includes("inspection") && k.includes("required")) ||
    (/\binspection\b/.test(label) && /\brequired\b/.test(label))
  ) {
    return 3;
  }
  if (k.includes("final_say") || /\bfinal say\b/.test(label)) return 4;
  return 99;
}

/** Features tab configure sheet order for permanent parking rules. */
export function sortPermanentParkingConfigurations<
  T extends {key: string; name?: string},
>(configs: T[]): T[] {
  return configs.toSorted((a, b) => {
    const order =
      getPermanentParkingRuleSortIndex(a.key, a.name) -
      getPermanentParkingRuleSortIndex(b.key, b.name);
    return order !== 0 ? order : a.key.localeCompare(b.key);
  });
}

/**
 * Maps select rule values to labels. API may store the option string or a 1-based index.
 * When `options` is missing, returns the raw string form of `value`.
 */
export function formatPropertyRuleSelectValue(
  value: unknown,
  options?: string[] | null,
): string {
  if (value === undefined || value === null || value === "") return "—";

  const asString = String(value).trim();
  if (!options?.length) return asString || "—";

  if (typeof value === "string" && options.includes(value)) return value;

  const numeric =
    typeof value === "number"
      ? value
      : /^-?\d+$/.test(asString)
        ? Number(asString)
        : Number.NaN;

  if (Number.isFinite(numeric)) {
    const oneBased = Math.trunc(numeric);
    if (oneBased >= 1 && oneBased <= options.length) return options[oneBased - 1]!;
    const zeroBased = Math.trunc(numeric);
    if (zeroBased >= 0 && zeroBased < options.length) return options[zeroBased]!;
  }

  return asString || "—";
}

export function normalizePropertyRuleOptions(raw: unknown): string[] | undefined {
  if (Array.isArray(raw)) {
    return raw.map((item) => String(item));
  }
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) return parsed.map((item) => String(item));
    } catch {
      return undefined;
    }
  }
  return undefined;
}

function isPeriodByRule(key: string, name?: string): boolean {
  const k = key.toLowerCase();
  if (k.includes("period_by") || k.includes("periodby")) return true;
  const label = (name ?? "").toLowerCase();
  return /\bperiod\b/.test(label) && /\bby\b/.test(label);
}

function isApplyByRule(key: string): boolean {
  const k = key.toLowerCase();
  return k.includes("apply_by") || k.includes("apply_mode");
}

/**
 * Known select option lists when `/me` rules omit `options[]` (matches property config modal).
 */
export function resolvePropertyRuleSelectOptions(
  key: string,
  apiOptions?: string[] | null,
  context?: {name?: string; feature?: string},
): string[] | undefined {
  const fromApi = normalizePropertyRuleOptions(apiOptions);
  if (fromApi?.length) return fromApi;

  const k = key.toLowerCase();
  const feature = (context?.feature ?? "").toLowerCase();
  const isHouseholds = feature === "households" || k.startsWith("occupancy_");
  const isSafelistings =
    feature.includes("safelisting") || k.startsWith("safelisting") || k.startsWith("safelistings");
  const isCitations = feature.includes("citation") || k.startsWith("citation");

  if (isHouseholds) {
    const householdOptions = resolveHouseholdSelectOptions(key);
    if (householdOptions?.length) return householdOptions;
  }

  if (isSafelistings) {
    if (isPeriodByRule(k, context?.name)) return [...SAFELISTING_PERIOD];
    if (k.includes("_by") || /\bby\b/i.test(context?.name ?? "")) return [...SAFELISTING_BY];
  }

  if (isCitations) {
    if (isApplyByRule(k)) return [...CITATION_APPLY_BY];
    if (isPeriodByRule(k, context?.name)) return [...CITATION_PERIOD];
    if (k.includes("_by") || /\bby\b/i.test(context?.name ?? "")) return [...CITATION_BY];
  }

  return undefined;
}
